import React, {
  createContext,
  useContext,
  useState,
  useEffect,
  useCallback,
  useRef,
} from "react";
import { authAPI } from "../api";

const AuthContext = createContext(null);

const INACTIVITY_TIMEOUT = 60 * 60 * 1000; // 1 hour in ms
const ACTIVITY_EVENTS = [
  "mousedown",
  "keydown",
  "scroll",
  "touchstart",
  "mousemove",
];

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) throw new Error("useAuth must be used within AuthProvider");
  return context;
};

export const AuthProvider = ({ children }) => {
  // Load cached user immediately from localStorage to prevent flash/logout on refresh
  const [user, setUser] = useState(() => {
    try {
      const cached = localStorage.getItem("pt_user");
      return cached ? JSON.parse(cached) : null;
    } catch {
      return null;
    }
  });
  const [loading, setLoading] = useState(true);
  const inactivityTimer = useRef(null);
  const lastActivity = useRef(Date.now());

  // Validate token with backend (non-blocking — user already loaded from cache)
  const loadUser = useCallback(async () => {
    const token = localStorage.getItem("pt_token");
    if (!token) {
      setUser(null);
      setLoading(false);
      return;
    }

    try {
      const { data } = await authAPI.getMe();
      const freshUser = data.data;
      setUser(freshUser);
      localStorage.setItem("pt_user", JSON.stringify(freshUser));
      // Update last activity timestamp on successful validation
      localStorage.setItem("pt_last_activity", Date.now().toString());
    } catch (err) {
      // Only clear session on explicit 401 (token invalid/expired)
      // Do NOT clear on network errors (server down, timeout, etc.)
      if (err.response && err.response.status === 401) {
        localStorage.removeItem("pt_token");
        localStorage.removeItem("pt_user");
        localStorage.removeItem("pt_last_activity");
        setUser(null);
      }
      // For network errors, keep the cached user — they'll revalidate on next API call
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    // Check if user was inactive for too long before this page load
    const lastActivityTime = parseInt(
      localStorage.getItem("pt_last_activity") || "0",
    );
    const elapsed = Date.now() - lastActivityTime;

    if (lastActivityTime > 0 && elapsed > INACTIVITY_TIMEOUT) {
      // Inactive for over 1 hour — log out
      localStorage.removeItem("pt_token");
      localStorage.removeItem("pt_user");
      localStorage.removeItem("pt_last_activity");
      setUser(null);
      setLoading(false);
      return;
    }

    loadUser();
  }, [loadUser]);

  // === Inactivity auto-logout ===
  const resetInactivityTimer = useCallback(() => {
    lastActivity.current = Date.now();
    localStorage.setItem("pt_last_activity", Date.now().toString());

    if (inactivityTimer.current) clearTimeout(inactivityTimer.current);

    // Only set timer if user is logged in
    const token = localStorage.getItem("pt_token");
    if (token) {
      inactivityTimer.current = setTimeout(() => {
        // Double check: if activity happened since timer was set, don't logout
        const elapsed = Date.now() - lastActivity.current;
        if (elapsed >= INACTIVITY_TIMEOUT - 1000) {
          localStorage.removeItem("pt_token");
          localStorage.removeItem("pt_user");
          localStorage.removeItem("pt_last_activity");
          setUser(null);
          // Redirect to login if on admin page
          if (window.location.pathname.startsWith("/admin")) {
            window.location.href = "/admin/login";
          }
        }
      }, INACTIVITY_TIMEOUT);
    }
  }, []);

  useEffect(() => {
    // Listen for user activity
    ACTIVITY_EVENTS.forEach((event) => {
      window.addEventListener(event, resetInactivityTimer, { passive: true });
    });

    // Also listen for activity in other tabs via storage event
    const handleStorage = (e) => {
      if (e.key === "pt_last_activity") {
        resetInactivityTimer();
      }
      // If another tab logs out, sync this tab
      if (e.key === "pt_token" && !e.newValue) {
        setUser(null);
      }
    };
    window.addEventListener("storage", handleStorage);

    // Start the timer
    resetInactivityTimer();

    return () => {
      ACTIVITY_EVENTS.forEach((event) => {
        window.removeEventListener(event, resetInactivityTimer);
      });
      window.removeEventListener("storage", handleStorage);
      if (inactivityTimer.current) clearTimeout(inactivityTimer.current);
    };
  }, [resetInactivityTimer]);

  const login = async (email, password) => {
    const { data } = await authAPI.login({ email, password });
    localStorage.setItem("pt_token", data.data.token);
    localStorage.setItem("pt_user", JSON.stringify(data.data.user));
    localStorage.setItem("pt_last_activity", Date.now().toString());
    setUser(data.data.user);
    resetInactivityTimer();
    return data.data.user;
  };

  const logout = () => {
    localStorage.removeItem("pt_token");
    localStorage.removeItem("pt_user");
    localStorage.removeItem("pt_last_activity");
    if (inactivityTimer.current) clearTimeout(inactivityTimer.current);
    setUser(null);
  };

  const isSuperAdmin = user?.role === "super_admin";
  const isHOD = user?.role === "hod";
  const userDepartmentId = user?.department_id;

  return (
    <AuthContext.Provider
      value={{
        user,
        loading,
        login,
        logout,
        isSuperAdmin,
        isHOD,
        userDepartmentId,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};
